import {
  DeleteButton,
  EditButton,
  ShowButton,
  TextField
} from '@refinedev/antd';
import { IClassroom } from 'interfaces/classrooms';
import { IInteractiveClass } from 'interfaces/interactive_classes';
import { IProfessor } from 'interfaces/professor';
import { Space, Table, TableProps, Tag } from 'antd';
import { formatDate } from 'services/date';

interface ListTableProps {
  tableProps: TableProps<IInteractiveClass>;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'created': {
      return 'grey';
    }

    case 'started': {
      return 'green';
    }

    case 'finished': {
      return 'grey';
    }

    case 'cancelled': {
      return 'red';
    }

    case 'suspended': {
      return 'yellow';
    }

    default: {
      return 'default';
    }
  }
};

const translateStatus = (status: string) => {
  switch (status) {
    case 'created': {
      return 'Criado';
    }

    case 'started': {
      return 'Iniciado';
    }

    case 'finished': {
      return 'Finalizado';
    }

    case 'cancelled': {
      return 'Cancelado';
    }

    case 'suspended': {
      return 'Suspensa';
    }

    default: {
      return status;
    }
  }
};

const formatEllipsis = (text: string, maxSize: number) => {
  if (text.length > maxSize) {
    return text.slice(0, maxSize) + '...';
  }
  return text;
};

export const ListTable = ({ tableProps }: ListTableProps) => {
  return (
    <Table
      {...tableProps}
      rowKey="id"
      pagination={{
        ...tableProps.pagination,
        // O data provider repassa o `x-total-count` cru, então `total` chega como string
        // apesar do tipo: compare pelo número.
        showTotal: (total, range) =>
          `Mostrando ${range[0]} a ${range[1]} de ${total} ${
            Number(total) === 1 ? 'resultado' : 'resultados'
          }`
      }}
    >
      <Table.Column
        dataIndex="id"
        key="id"
        title="ID da reunião"
        render={value => <TextField value={value} />}
      />
      <Table.Column
        dataIndex="title"
        key="title"
        title="Título"
        render={value => <TextField value={value} />}
      />
      <Table.Column
        dataIndex="professors"
        key="professors"
        title="Professor"
        render={professors => (
          <TextField
            value={formatEllipsis(
              professors
                .map((professor: IProfessor) => professor.name)
                .join(', '),
              30
            )}
          />
        )}
      />
      <Table.Column
        dataIndex="classrooms"
        key="classrooms"
        title="Turmas"
        render={classrooms => (
          <TextField
            value={formatEllipsis(
              classrooms
                .map(
                  (classroom: IClassroom) =>
                    classroom.discipline.name + ' - ' + classroom.code
                )
                .join(', '),
              30
            )}
          />
        )}
      />
      <Table.Column
        dataIndex="start_time"
        key="start_time"
        title="Data"
        render={value => <TextField value={formatDate(value)} />}
      />
      <Table.Column
        dataIndex="status"
        key="status"
        title="Status"
        render={value => (
          <Tag color={getStatusColor(value)}>{translateStatus(value)}</Tag>
        )}
      />
      <Table.Column<IInteractiveClass>
        title="Ações"
        dataIndex="actions"
        render={(_, record) => {
          return (
            <Space>
              <ShowButton
                hideText
                size="small"
                recordItemId={record.id}
                accessControl={{ hideIfUnauthorized: true }}
              />

              {['created'].includes(record.status) && (
                <>
                  <EditButton
                    hideText
                    size="small"
                    recordItemId={record.id}
                    accessControl={{ hideIfUnauthorized: true }}
                  />
                  <DeleteButton
                    size="small"
                    recordItemId={record.id}
                    hideText
                    accessControl={{ hideIfUnauthorized: true }}
                    successNotification={{
                      type: 'success',
                      description: 'Sucesso!',
                      message: 'Aula interativa cancelada com sucesso.'
                    }}
                  />
                </>
              )}
            </Space>
          );
        }}
      />
    </Table>
  );
};
